import { motion, useReducedMotion } from "framer-motion";
import { Volume2 } from "lucide-react";
import { Section, Headline, Reveal } from "@/components/primitives";
import { cases } from "@/config";
import { useVoice } from "@/context/VoiceContext";
import { stagger, item, viewport } from "@/lib/motion";

export default function Cases() {
  const { openCase } = useVoice();
  const reduce = useReducedMotion();

  const listen = (e, i) => {
    e.stopPropagation();
    openCase(i);
    // the modal registers its own player after mount; nudge it on the next frame
    setTimeout(() => window.dispatchEvent(new CustomEvent("bca:case-listen")), 450);
  };

  return (
    <Section id="cases" tone="noir">
      <Reveal>
        <Headline>{cases.headline}</Headline>
        <p className="mt-4 text-sm text-gray2">{cases.note}</p>
      </Reveal>

      <motion.div
        variants={reduce ? undefined : stagger(0.1)} initial={reduce ? false : "hidden"} whileInView="show" viewport={viewport}
        className="mt-14 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4"
      >
        {cases.items.map((c, i) => (
          <motion.article
            key={c.id}
            variants={item}
            data-testid={`case-card-${c.id}`}
            onClick={() => openCase(i)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openCase(i)}
            role="button" tabIndex={0}
            className="card card-hover group flex h-full cursor-pointer flex-col rounded-xl p-6"
          >
            <div className="flex items-start justify-between">
              <span className="stamp">{c.badge}</span>
              <button
                data-testid={`case-listen-${c.id}`}
                onClick={(e) => listen(e, i)}
                aria-label={`Listen to ${c.title}`}
                className="grid h-8 w-8 place-items-center rounded-full border border-cream/10 text-gray2 transition-colors hover:border-gold hover:text-gold"
              >
                <Volume2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <h3 className="mt-6 font-serif text-2xl leading-tight text-cream">{c.title}</h3>
            <p className="mt-3 flex-1 text-sm leading-relaxed text-cream/65">{c.hook}</p>
            <div className="mt-6 flex flex-wrap gap-1.5">
              {c.chips.map((ch) => <span key={ch} className="chip">{ch}</span>)}
            </div>
            <span className="mt-6 text-xs text-gold/70 transition-colors group-hover:text-gold">Open the file →</span>
          </motion.article>
        ))}
      </motion.div>
    </Section>
  );
}
